import React from "react";
import { FieldProps } from "../../../types/FormTypes";
import DateTimeInput, { type DateTimeInputProps } from "../DateTimeInput/DateTimeInput";
import Field from "../Field/Field";
import { useField } from "../Form/Form";
import { composeRefs } from "@radix-ui/react-compose-refs";
import { withRefProp } from "../../../utils/withRefProp";

export interface DateTimeFieldProps<TValue = string>
  extends Omit<FieldProps<TValue>, "error">,
    Omit<DateTimeInputProps<TValue>, "value" | "onValueChange" | "onChange" | "name" | "error"> {
  error?: boolean | string;
}

const DateTimeField = withRefProp(<TValue = string,>(props: DateTimeFieldProps<TValue>) => {
  const {
    className,
    name,
    label,
    helperText,
    required,
    disabled,
    fullWidth = false,
    value: controlledValue,
    error,
    onChange,
    onValueChange,
    onBlur,
    ref,
    ...otherProps
  } = props;
  const field = useField<TValue>({ name, value: controlledValue, onChange, onValueChange, onBlur });
  const errorMessage = typeof error === "string" ? error : undefined;
  const hasError = Boolean(field.error || errorMessage || (typeof error === "boolean" ? error : false));

  return (
    <Field
      className={className}
      name={name}
      fullWidth={fullWidth}
    >
      {label ? <Field.Label required={required ?? field.required}>{label}</Field.Label> : null}
      <Field.Control>
        <DateTimeInput
          ref={composeRefs(field.ref as any, ref)}
          value={field.value}
          onValueChange={field.onValueChange}
          onBlur={field.onBlur}
          disabled={disabled}
          fullWidth={fullWidth}
          error={hasError}
          {...otherProps}
        />
      </Field.Control>
      <Field.Message
        error={hasError}
        message={errorMessage ?? helperText}
      />
    </Field>
  );
});

export default DateTimeField;
